import { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Search, UserPlus, AlertTriangle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../services/authService";
import VehicleLookup from "../components/VehicleLookup";
import {
  validatePartitaIVA,
  validateCodiceFiscale,
  validateCAP,
  validateProvincia,
} from "../utils/fiscalValidators";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY_ITEM = { description: "", quantity: 1, unit_price: "", vat_rate: 22, type: "RICAMBIO" };

const PAYMENT_METHODS = [
  { code: "MP01", label: "Contanti" },
  { code: "MP02", label: "Assegno" },
  { code: "MP05", label: "Bonifico" },
  { code: "MP08", label: "Carta di credito" },
  { code: "MP19", label: "SEPA Direct Debit" },
];

export default function DirectInvoiceForm({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const searchRef = useRef(null);

  const [form, setForm] = useState({
    customer_id: "new",
    customer_type: "B2C",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    customer_city: "",
    customer_province: "",
    customer_cap: "",
    customer_fiscal_code: "",
    customer_vat_number: "",
    customer_sdi_code: "",
    customer_pec: "",
    cig: "",
    cup: "",
    vehicle_plate: "",
    vehicle_model: "",
    items: [{ ...EMPTY_ITEM }],
    notes: "",
    payment_method: "MP05",
  });

  useEffect(() => {
    authFetch(`${API}/customers`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : data.customers || [];
        setCustomers(list);
      })
      .catch(() => {});
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = searchTerm.length > 0
    ? customers.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (c.fiscal_code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
          (c.vat_number || "").toLowerCase().includes(searchTerm.toLowerCase())
      )
    : customers;

  const selectCustomer = (c) => {
    setIsNewCustomer(false);
    setShowDropdown(false);
    setSearchTerm("");
    setForm((prev) => ({
      ...prev,
      customer_id: c.customer_id || c.id,
      customer_type: c.customer_type || c.type || "B2C",
      customer_name: c.name || "",
      customer_email: c.email || "",
      customer_phone: c.phone || "",
      customer_address: c.address || "",
      customer_city: c.city || "",
      customer_province: c.province || "",
      customer_cap: c.cap || c.zip_code || "",
      customer_fiscal_code: c.fiscal_code || "",
      customer_vat_number: c.vat_number || "",
      customer_sdi_code: c.sdi_code || "",
      customer_pec: c.pec || "",
    }));
    setSearchTerm("");
    setFieldErrors({});
  };

  const resetCustomer = () => {
    setIsNewCustomer(true);
    setShowDropdown(false);
    setSearchTerm("");
    setForm((prev) => ({
      ...prev,
      customer_id: "new",
      customer_type: "B2C",
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      customer_address: "",
      customer_city: "",
      customer_province: "",
      customer_cap: "",
      customer_fiscal_code: "",
      customer_vat_number: "",
      customer_sdi_code: "",
      customer_pec: "",
      cig: "",
      cup: "",
    }));
    setFieldErrors({});
    toast.info("Compila i dati del nuovo cliente");
  };

  const validateField = (field, value) => {
    let result = { isValid: true };
    if (field === "customer_vat_number" && value) result = validatePartitaIVA(value);
    if (field === "customer_fiscal_code" && value) result = validateCodiceFiscale(value);
    if (field === "customer_cap" && value) result = validateCAP(value);
    if (field === "customer_province" && value) result = validateProvincia(value);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (result.isValid || !value) delete next[field];
      else next[field] = result.error;
      return next;
    });
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const updateItem = (idx, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) =>
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce(
    (sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
    0
  );
  const vatTotal = form.items.reduce((sum, it) => {
    const base = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
    return sum + base * ((parseFloat(it.vat_rate) || 22) / 100);
  }, 0);
  const total = subtotal + vatTotal;

  const handleSubmit = async () => {
    if (!form.customer_name.trim()) return toast.error("Nome cliente obbligatorio");
    if (form.items.length === 0) return toast.error("Aggiungere almeno un articolo");
    if (Object.keys(fieldErrors).length > 0) return toast.error("Correggere i campi evidenziati");

    setLoading(true);
    try {
      // Clean items: ensure numeric values
      const cleanItems = form.items.map(it => ({
        ...it,
        quantity: parseFloat(it.quantity) || 1,
        unit_price: parseFloat(it.unit_price) || 0,
        vat_rate: parseFloat(it.vat_rate) || 22,
      }));
      const res = await authFetch(`${API}/invoices/direct`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, items: cleanItems }),
      });
      if (res.ok) {
        const inv = await res.json();
        toast.success(`Fattura ${inv.invoice_number} creata`);
        onCreated?.(inv);
        onClose();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nella creazione");
      }
    } catch {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (field) =>
    `form-input text-sm ${fieldErrors[field] ? "border-red-500 ring-1 ring-red-500/30" : ""}`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8">
      <div
        data-testid="direct-invoice-form"
        className="bg-[var(--bg-secondary)] border border-[var(--border-primary)] rounded-sm w-full max-w-4xl mx-4 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-primary)]">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Nuova Fattura Diretta</h2>
          <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* CLIENTE */}
          <section>
            <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">
              Dati Cliente
            </h3>

            <div className="flex gap-2 mb-3">
              <div className="relative flex-1" ref={searchRef}>
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-[var(--text-muted)]" />
                <input
                  data-testid="customer-search"
                  type="text"
                  placeholder="Cerca cliente esistente..."
                  className="form-input text-sm pl-8"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                />
                {showDropdown && filtered.length > 0 && (
                  <div className="absolute z-[100] w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-sm shadow-2xl max-h-52 overflow-y-auto" style={{boxShadow: '0 10px 40px rgba(0,0,0,0.8)'}}>
                    {filtered.slice(0, 20).map((c) => (
                      <button
                        key={c.customer_id || c.id}
                        className="w-full text-left px-3 py-2.5 hover:bg-zinc-800 text-sm border-b border-zinc-800 last:border-0"
                        onClick={() => selectCustomer(c)}
                      >
                        <span className="font-medium text-white">{c.name}</span>
                        <span className="text-zinc-400 ml-2 text-xs">
                          {c.customer_type === "B2B" ? "Azienda" : c.customer_type === "PA" ? "PA" : "Privato"}
                          {c.fiscal_code ? ` - ${c.fiscal_code}` : c.vat_number ? ` - P.IVA ${c.vat_number}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={resetCustomer} data-testid="new-customer-btn"
                className={isNewCustomer ? "border-[var(--accent-primary)] text-[var(--accent-primary)]" : ""}>
                <UserPlus className="w-4 h-4 mr-1" /> Nuovo
              </Button>
            </div>

            {!isNewCustomer && (
              <p className="text-xs text-green-400 mb-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> Cliente selezionato: {form.customer_name}
              </p>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="form-label text-xs">Nome / Ragione Sociale *</label>
                <input
                  data-testid="di-customer-name"
                  className="form-input text-sm"
                  value={form.customer_name}
                  onChange={(e) => updateField("customer_name", e.target.value)}
                />
              </div>
              <div>
                <label className="form-label text-xs">Tipo Cliente</label>
                <select
                  data-testid="di-customer-type"
                  className="form-input text-sm"
                  value={form.customer_type}
                  onChange={(e) => updateField("customer_type", e.target.value)}
                >
                  <option value="B2C">Privato (B2C)</option>
                  <option value="B2B">Azienda (B2B)</option>
                  <option value="PA">Pubblica Amministrazione</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="form-label text-xs">Codice Fiscale</label>
                <input
                  data-testid="di-cf"
                  className={inputCls("customer_fiscal_code") + " uppercase"}
                  value={form.customer_fiscal_code}
                  onChange={(e) => updateField("customer_fiscal_code", e.target.value.toUpperCase())}
                />
                {fieldErrors.customer_fiscal_code && (
                  <p className="text-xs text-red-400 mt-0.5">{fieldErrors.customer_fiscal_code}</p>
                )}
              </div>
              <div>
                <label className="form-label text-xs">Partita IVA</label>
                <input
                  data-testid="di-vat"
                  className={inputCls("customer_vat_number") + " uppercase"}
                  value={form.customer_vat_number}
                  onChange={(e) => updateField("customer_vat_number", e.target.value.toUpperCase())}
                />
                {fieldErrors.customer_vat_number && (
                  <p className="text-xs text-red-400 mt-0.5">{fieldErrors.customer_vat_number}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mt-2">
              <div className="col-span-2">
                <label className="form-label text-xs">Indirizzo *</label>
                <input className="form-input text-sm" value={form.customer_address}
                  onChange={(e) => updateField("customer_address", e.target.value)} />
              </div>
              <div>
                <label className="form-label text-xs">Citta *</label>
                <input className="form-input text-sm" value={form.customer_city}
                  onChange={(e) => updateField("customer_city", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="form-label text-xs">CAP</label>
                  <input className={inputCls("customer_cap")} value={form.customer_cap}
                    onChange={(e) => updateField("customer_cap", e.target.value)} maxLength={5} />
                </div>
                <div>
                  <label className="form-label text-xs">Prov</label>
                  <input className={inputCls("customer_province") + " uppercase"} value={form.customer_province}
                    onChange={(e) => updateField("customer_province", e.target.value.toUpperCase())} maxLength={2} />
                </div>
              </div>
            </div>

            {(form.customer_type === "B2B" || form.customer_type === "PA") && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="form-label text-xs">{form.customer_type === "PA" ? "Codice IPA" : "Codice SDI"}</label>
                  <input className="form-input text-sm uppercase" value={form.customer_sdi_code}
                    onChange={(e) => updateField("customer_sdi_code", e.target.value.toUpperCase())}
                    maxLength={form.customer_type === "PA" ? 6 : 7} />
                </div>
                <div>
                  <label className="form-label text-xs">PEC</label>
                  <input className="form-input text-sm" value={form.customer_pec}
                    onChange={(e) => updateField("customer_pec", e.target.value)} />
                </div>
              </div>
            )}
            {form.customer_type === "PA" && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="form-label text-xs">CIG *</label>
                  <input className="form-input text-sm uppercase" value={form.cig}
                    onChange={(e) => updateField("cig", e.target.value.toUpperCase())} maxLength={10} />
                </div>
                <div>
                  <label className="form-label text-xs">CUP</label>
                  <input className="form-input text-sm uppercase" value={form.cup}
                    onChange={(e) => updateField("cup", e.target.value.toUpperCase())} maxLength={15} />
                </div>
              </div>
            )}
          </section>

          {/* VEICOLO con lookup targa */}
          <section>
            <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">
              Veicolo (opzionale)
            </h3>
            <VehicleLookup
              initialPlate={form.vehicle_plate}
              showDetails={true}
              onVehicleFound={(data) => {
                if (data) {
                  setForm((prev) => ({
                    ...prev,
                    vehicle_plate: data.licensePlate || prev.vehicle_plate,
                    vehicle_model: [data.brand, data.model].filter(Boolean).join(" ") || prev.vehicle_model,
                  }));
                }
              }}
            />
            {form.vehicle_model && (
              <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                <Check className="w-3 h-3" /> {form.vehicle_plate} - {form.vehicle_model}
              </p>
            )}
          </section>

          {/* ARTICOLI */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider">
                Articoli / Servizi
              </h3>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>

            <div className="space-y-2">
              {form.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4">
                    {idx === 0 && <label className="form-label text-xs">Descrizione</label>}
                    <input
                      data-testid={`item-desc-${idx}`}
                      className="form-input text-sm"
                      placeholder="Descrizione..."
                      value={item.description}
                      onChange={(e) => updateItem(idx, "description", e.target.value)}
                    />
                  </div>
                  <div className="col-span-1">
                    {idx === 0 && <label className="form-label text-xs">Tipo</label>}
                    <select className="form-input text-sm" value={item.type}
                      onChange={(e) => updateItem(idx, "type", e.target.value)}>
                      <option value="RICAMBIO">Ricambio</option>
                      <option value="MANODOPERA">Manodopera</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <label className="form-label text-xs">Q.ta</label>}
                    <input type="number" min="0" step="0.5" className="form-input text-sm text-center"
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} />
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <label className="form-label text-xs">Prezzo</label>}
                    <input type="number" min="0" step="0.01" className="form-input text-sm text-right"
                      value={item.unit_price}
                      onChange={(e) => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} />
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <label className="form-label text-xs">IVA %</label>}
                    <select className="form-input text-sm" value={item.vat_rate}
                      onChange={(e) => updateItem(idx, "vat_rate", parseFloat(e.target.value))}>
                      <option value={22}>22%</option>
                      <option value={10}>10%</option>
                      <option value={4}>4%</option>
                      <option value={0}>Esente</option>
                    </select>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {form.items.length > 1 && (
                      <button onClick={() => removeItem(idx)}
                        className="text-red-400 hover:text-red-300 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* RIEPILOGO + NOTE */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label text-xs">Metodo Pagamento</label>
              <select data-testid="di-payment" className="form-input text-sm" value={form.payment_method}
                onChange={(e) => updateField("payment_method", e.target.value)}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.code} value={m.code}>{m.label}</option>
                ))}
              </select>
              <label className="form-label text-xs mt-2">Note</label>
              <textarea className="form-input text-sm" rows={2} value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)} />
            </div>
            <div className="bg-[var(--bg-primary)] p-3 rounded-sm border border-[var(--border-primary)]">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[var(--text-muted)]">Imponibile</span>
                <span>{subtotal.toFixed(2)} EUR</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[var(--text-muted)]">IVA</span>
                <span>{vatTotal.toFixed(2)} EUR</span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-[var(--border-primary)] pt-2 mt-2">
                <span>Totale</span>
                <span className="text-[var(--accent-primary)]">{total.toFixed(2)} EUR</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-[var(--border-primary)]">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button
            data-testid="create-direct-invoice-btn"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creazione..." : "Crea Fattura"}
          </Button>
        </div>
      </div>
    </div>
  );
}
